import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box } from '@chakra-ui/react';
import type { LandingLayoutType } from 'ts/client/types';
import { LoadingIndicator } from 'ts/common/components';
import BaseLayout from 'ts/common/components/gallery/layouts/BaseLayout';
import useCoverPhotoUrl from 'ts/common/hooks/useCoverPhotoUrl';
import type { ICoverPhoto } from 'ts/common/types';
import { noop } from 'ts/common/utils';

export interface IIntroProps {
    landingLayoutType: LandingLayoutType;
    galleryTitle: string;
    brandName: string;
    eventTheme?: SpApi.Client.IEventTheme;
    coverPhoto: ICoverPhoto | false;
    isAdminRoute: boolean;
    isPreRegister: boolean;
    translateFilter: SpAngularJs.ITranslateFilter;
    onClick: () => void;
}

const Intro: FC<IIntroProps> = ({
    eventTheme,
    isPreRegister,
    isAdminRoute,
    translateFilter: t,
    ...layoutProps
}) => {
    const { coverPhoto } = layoutProps;
    const [isCoverPhotoLoaded, setIsCoverPhotoLoaded] = useState(false);

    const coverPhotoUrl = useCoverPhotoUrl(coverPhoto);

    useEffect(() => {
        let imageLoader: HTMLImageElement | undefined;

        if (coverPhotoUrl) {
            imageLoader = new Image();

            imageLoader.src = coverPhotoUrl;

            imageLoader.onload = () => {
                setIsCoverPhotoLoaded(true);
            };
        } else {
            setIsCoverPhotoLoaded(true);
        }

        return () => {
            if (imageLoader) {
                imageLoader.onload = noop;
            }
        };
    }, [coverPhotoUrl]);

    const buttonTextLarge = useMemo(() => {
        if (isPreRegister && !isAdminRoute) {
            return t('register');
        }

        if (isPreRegister && isAdminRoute) {
            return t('enterAdminMode');
        }

        return t('viewGallery');
    }, [t, isAdminRoute, isPreRegister]);

    const buttonText = useMemo(() => {
        if (isPreRegister && !isAdminRoute) {
            return t('register');
        }

        if (isPreRegister && isAdminRoute) {
            return t('enter');
        }

        return t('viewGallery');
    }, [t, isAdminRoute, isPreRegister]);

    if (!isCoverPhotoLoaded || !eventTheme) {
        return (
            <Box
                position="fixed"
                transform="translate(-50%, -50%)"
                top="50vh"
                left="50vw"
                width="full"
            >
                <LoadingIndicator squareClassName="theme-background-color-primary" />
            </Box>
        );
    }

    return (
        <BaseLayout
            {...layoutProps}
            buttonText={buttonText}
            buttonTextLarge={buttonTextLarge}
            colorScheme={eventTheme.colorScheme}
            coverPhotoUrl={coverPhotoUrl}
            eventBrandLogo={eventTheme.eventBrandLogo}
            logoAltText={eventTheme.logoAltText}
        />
    );
};

export default Intro;
